import { memo, useCallback } from 'react';

import { ChevronDown } from 'lucide-react';

import { Badge } from '@/shadcn/badge';
import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';

import { TestId } from '@/types/testId';
import { pluralize } from '@/utils';
import { useToggle } from '@/utils/hooks';
import { ParticipantCard } from './participantCard/participantCard';

type Props = {
  title?: string;
  participants: any[];
  teamAccountId?: string | null;
  removeGroupMembers: (accountIds: string[], title: string) => void;
};

export const ParticipantsList = memo(
  ({
    title = '',
    participants,
    teamAccountId = null,
    removeGroupMembers
  }: Props) => {
    const [displayParticipants, toggleDisplayParticipants] = useToggle(true);
    const hasParticipants = participants.length > 0;

    const removeAll = useCallback(() => {
      removeGroupMembers(
        teamAccountId
          ? [teamAccountId]
          : participants.map(participant => participant.accountId),
        title
      );
    }, [participants, teamAccountId, removeGroupMembers]);

    return (
      <div className="mt-2.5 flex flex-col flex-nowrap items-stretch">
        <div className="flex flex-row flex-nowrap items-center justify-between">
          <div
            className={cn(
              'my-2 flex flex-row flex-nowrap items-center gap-3 font-semibold text-base text-foreground capitalize',
              hasParticipants && 'cursor-pointer'
            )}
            data-cy={TestId.ParticipantsList.ListToggle}
            onClick={hasParticipants ? toggleDisplayParticipants : undefined}
          >
            <p>{title ?? 'Undefined team name'}</p>

            {hasParticipants && (
              <ChevronDown
                className={cn(
                  'shrink-0 transition-transform ease-out',
                  !displayParticipants && 'rotate-180'
                )}
                size={18}
              />
            )}
            <Badge
              className="pointer-events-none cursor-default select-none text-xs normal-case"
              tabIndex={-1}
              variant="card"
            >
              {pluralize(participants.length, 'member')}
            </Badge>
          </div>
          <Button
            className="normal-case"
            data-cy={TestId.ParticipantsList.RemoveAllButton}
            onClick={removeAll}
            size="sm"
            variant="ghostDestructiveSubtle"
          >
            {`Remove ${teamAccountId ? 'team' : 'all'}`}
          </Button>
        </div>
        {hasParticipants && displayParticipants && (
          <div className="flex flex-col gap-2">
            {participants.map(({ accountId, entity: participant }) => (
              <div key={participant.rowId}>
                <ParticipantCard
                  participant={participant}
                  participantAccountId={accountId} // note: if within a team, the participant has an account id of null and hence will not be removable
                  removeGroupMembers={removeGroupMembers}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);
